import { create } from 'zustand';
import type { Journey, UserJourney } from '@breath-lab/types';
import { getServices } from '@breath-lab/services';
import { isDev } from '@breath-lab/domain';

const DEBUG_JOURNEY: Journey | null = isDev
  ? {
      id: 'DEBUG_PLAYER_TEST',
      position: 9999,
      isEnabled: true,
      name: { en: '⚙ Debug: Player Transitions' },
      description: { en: 'Local debug journey — not from Firestore.' },
      goal: { en: 'Observe technique transitions in the player.' },
      difficulty: 'beginner',
      category: 'general',
      totalSteps: 1,
      activitiesIds: [],
      benefitsIds: [],
      steps: [
        {
          id: 'debug_step_1',
          position: 1,
          title: { en: 'Transition Test' },
          description: { en: 'Ujjayi (1 round) → Sama Vritti (1 round). Observe the interstitial.' },
          techniques: [
            {
              breathworkId: 'UJJAYI',
              configOverride: {
                rounds: 1,
                breathDuration: { inhaleMs: 4000, fullLungsRetentionMs: 0, exhaleMs: 4000, emptyLungsRetentionMs: 0 },
              },
            },
            {
              breathworkId: 'SAMA_VRITTI',
              configOverride: {
                rounds: 1,
                breathDuration: { inhaleMs: 4000, fullLungsRetentionMs: 4000, exhaleMs: 4000, emptyLungsRetentionMs: 4000 },
              },
            },
          ],
        },
      ],
    }
  : null;

export interface JourneyWithState {
  journey: Journey;
  userJourney: UserJourney | null;
}

interface JourneysState {
  journeys: JourneyWithState[];
  freeIds: string[];
  paywallEnabled: boolean;
  isLoaded: boolean;
  isLoading: boolean;
  error: boolean;
  loadedForUserId: string | null;

  // selector
  activeJourney: () => JourneyWithState | null;

  // actions
  load: (userId: string) => Promise<void>;
  loadTemplates: () => Promise<void>;
  reload: (userId: string) => Promise<void>;
  reset: () => void;
}

async function fetchJourneys(userId: string): Promise<JourneyWithState[]> {
  const [templates, userJourneysResult] = await Promise.all([
    getServices().data.fetchJourneyTemplates(),
    getServices().userData.fetchUserJourneys(userId).catch(() => []),
  ]);

  const result = templates
    .sort((a, b) => a.position - b.position)
    .map((journey) => ({
      journey,
      userJourney: userJourneysResult.find((uj) => uj.sourceJourneyId === journey.id) ?? null,
    }));

  if (DEBUG_JOURNEY) result.unshift({ journey: DEBUG_JOURNEY, userJourney: null });
  return result;
}

export const useJourneys = create<JourneysState>((set, get) => ({
  journeys: [],
  freeIds: [],
  paywallEnabled: false,
  isLoaded: false,
  isLoading: false,
  error: false,
  loadedForUserId: null,

  activeJourney: () => {
    return (
      get().journeys.find(
        ({ userJourney }) =>
          userJourney?.status === 'active' || userJourney?.status === 'paused',
      ) ?? null
    );
  },

  loadTemplates: async () => {
    if (get().journeys.length > 0) return;
    set({ isLoading: true, error: false });
    try {
      const [templates, { freeJourneysIds, paywallEnabled }] = await Promise.all([
        getServices().data.fetchJourneysPublic(),
        getServices().remoteConfig.fetchCatalogGates(),
      ]);
      const journeys: JourneyWithState[] = templates
        .sort((a, b) => a.position - b.position)
        .map((journey) => ({ journey, userJourney: null }));
      if (DEBUG_JOURNEY) journeys.unshift({ journey: DEBUG_JOURNEY, userJourney: null });
      const freeIds = DEBUG_JOURNEY ? [...freeJourneysIds, DEBUG_JOURNEY.id] : freeJourneysIds;
      set({ journeys, freeIds, paywallEnabled, isLoading: false });
    } catch {
      set({ error: true, isLoading: false });
    }
  },

  load: async (userId) => {
    const { isLoaded, loadedForUserId, isLoading } = get();
    if ((isLoaded && loadedForUserId === userId) || isLoading) return;

    set({ isLoading: true, error: false });
    try {
      const [merged, { freeJourneysIds, paywallEnabled }] = await Promise.all([
        fetchJourneys(userId),
        getServices().remoteConfig.fetchCatalogGates(),
      ]);
      const freeIds = DEBUG_JOURNEY ? [...freeJourneysIds, DEBUG_JOURNEY.id] : freeJourneysIds;
      set({ journeys: merged, freeIds, paywallEnabled, isLoaded: true, isLoading: false, loadedForUserId: userId });
    } catch {
      set({ isLoading: false, error: true });
    }
  },

  reload: async (userId) => {
    set({ isLoading: true, error: false, loadedForUserId: null });
    try {
      const [merged, { freeJourneysIds, paywallEnabled }] = await Promise.all([
        fetchJourneys(userId),
        getServices().remoteConfig.fetchCatalogGates(),
      ]);
      const freeIds = DEBUG_JOURNEY ? [...freeJourneysIds, DEBUG_JOURNEY.id] : freeJourneysIds;
      set({ journeys: merged, freeIds, paywallEnabled, isLoading: false, loadedForUserId: userId });
    } catch {
      set({ isLoading: false, error: true });
    }
  },

  reset: () => {
    set({ journeys: [], isLoaded: false, isLoading: false, error: false, loadedForUserId: null });
  },
}));
